
// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@hw-ui/components/toastification/ToastificationContent.vue'

import store from '@/shared/store'
import i18n from '@hw-core/libs/i18n'

export default function useManageShiftSidebar(emit) {
  // Use toast
  const toast = useToast()

  const fetchUserUpcomingShifts = ({ assignedTo, callback }) => {
    store
      .dispatch('appCalendar/fetchUserUpcomingShifts', {
        assignedTo,
      })
      .then(shifts => {
        callback(shifts)
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('error'),
            text: 'Virhe vuorojen hakemisessa',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const onSearchLocation = ({ text, accountId, callback }) => {
    if (text) {
      store.dispatch('appCalendar/fetchLocation', { text, accountId }).then(data => {
        callback(data)
      })
    }
  }

  const searchUsers = ({ q, callback }) => {
    store
      .dispatch('appCalendar/searchUsers', {
        q,
      })
      .then(users => {
        callback(users)
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('error'),
            text: 'Virhe käyttäjien hakemisessa',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const fetchLocationDetails = async ({ id, assignedTo, callback }) => {
    try {
      const gig = await store.dispatch('appCalendar/fetchLocationDetails', { id })
      let user = null
      if (assignedTo && assignedTo.length > 2) {
        user = await store.dispatch('appCalendar/fetchUserDetails', { assignedTo })
      }
      callback({ ...gig, user })
    } catch {
      toast({
        component: ToastificationContent,
        props: {
          title: i18n.t('error'),
          text: i18n.t('something_went_wrong'),
          icon: 'AlertTriangleIcon',
          variant: 'danger',
        },
      })
    }
  }
  // ------------------------------------------------
  // updateShift
  // ------------------------------------------------
  const updateShift = shiftData => {
    store.dispatch('appCalendar/updateShift', { shift: shiftData }).then(() => {
      toast({
        component: ToastificationContent,
        props: {
          title: i18n.t('success'),
          text: 'Vuoro päivitetty',
          icon: 'CheckIcon',
          variant: 'success',
        },
      })
      emit('onUpdated')
    })
  }

  const createGigAndUpdateShift = shiftData => {
    store.dispatch('appCalendar/createGigAndUpdateShift', { shift: shiftData }).then(() => {
      toast({
        component: ToastificationContent,
        props: {
          title: i18n.t('success'),
          text: 'Vuoro päivitetty',
          icon: 'CheckIcon',
          variant: 'success',
        },
      })
      emit('onUpdated')
    })
  }

  const sendVerification = ({
    to, message, subject, callback,
  }) => {
    store.dispatch('appCalendar/sendEmail', {
      to,
      message,
      subject,
    })
      .then(() => {
        callback()
        emit('onUpdated')
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('success'),
            text: 'Vahvistusviesti lähetetty',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('error'),
            text: i18n.t('something_went_wrong'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const publishGig = gigId => {
    store.dispatch('appCalendar/publishGig', {
      gigId,
    })
      .then(() => {
        emit('onUpdated')
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('success'),
            text: 'Vuorot luotu',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('error'),
            text: i18n.t('something_went_wrong'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  const publishShifts = ({ publishAll, shift, callback }) => {
    if(!publishAll) {
      createGigAndUpdateShift(shift)
      callback()
    } else {
      publishGig(shift.extendedProps.gigId)
      callback()
    }
  }

  const removeEvent = (eventId, processer) => {
    store.dispatch('appCalendar/deleteShift', { id: eventId, processer }).then(() => {
      toast({
        component: ToastificationContent,
        props: {
          title: i18n.t('success'),
          text: 'Vuoro poistettu',
          icon: 'CheckIcon',
          variant: 'success',
        },
      })
      emit('onUpdated')
    })
  }

  const sendCancelNotification = ({
    email, mobile, emailMessage, smsMessage, subject, callback,
  }) => {
    // Send Email
    callback()
    store.dispatch('appCalendar/sendEmail', {
      to: email,
      message: emailMessage,
      subject,
    })
      .then(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('success'),
            text: 'Sähköposti lähetetty',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('error'),
            text: i18n.t('something_went_wrong'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })

    // Send SMS
    store.dispatch('appCalendar/sendSMS', {
      mobile,
      message: smsMessage,
    })
      .then(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('success'),
            text: 'Tekstiviesti lähetetty',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('error'),
            text: i18n.t('something_went_wrong'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
  }

  return {
    updateShift,
    fetchLocationDetails,
    searchUsers,
    sendVerification,
    sendCancelNotification,
    publishShifts,
    createGigAndUpdateShift,
    fetchUserUpcomingShifts,
    removeEvent,
    onSearchLocation,
  }
}
